import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import api from '../../api';
import { RfpExtraction } from '../../api/rfpParser';

interface Props {
  clientNameHint?: string;
  onApply?: (extraction: RfpExtraction) => void;
  onClose: () => void;
  open: boolean;
}

const confidenceColor = (c?: number): string => {
  if (c == null) return '#757575';
  if (c >= 0.75) return '#1D7A3B';
  if (c >= 0.5) return '#C06A00';
  return '#B00020';
};

const confidenceLabel = (c?: number): string => {
  if (c == null) return 'Unknown';
  if (c >= 0.75) return 'High';
  if (c >= 0.5) return 'Medium';
  return 'Low';
};

const money = (n?: number): string =>
  n == null
    ? '—'
    : `$${n.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      })}`;

/**
 * Calculator v3 — AI-assisted RFP parser drawer.
 *
 * Operator pastes the client's RFP / scope-of-work into the textarea,
 * hits Extract, and reviews the structured fields Claude extracted.
 * Apply writes the fields into the calling context (e.g. pre-fills the
 * Workloading inputs + scope checklist) via the onApply callback.
 */
const ParseRfpDrawerDefaults = {
  clientNameHint: undefined,
  onApply: undefined
};
const ParseRfpDrawer: React.FC<Props> = function ParseRfpDrawer({
  clientNameHint,
  onApply,
  onClose,
  open
}) {
  const [rfpText, setRfpText] = useState<string>('');
  const [extracting, setExtracting] = useState<boolean>(false);
  const [extraction, setExtraction] = useState<RfpExtraction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setRfpText('');
    setExtraction(null);
    setError(null);
  };

  const run = async () => {
    if (!rfpText.trim() || rfpText.trim().length < 20) {
      setError('Paste at least 20 characters of RFP text.');
      return;
    }
    setError(null);
    setExtracting(true);
    try {
      const res = await api.RfpParserApi.extract({
        clientNameHint,
        rfpText: rfpText.trim()
      });
      setExtraction(res.extraction);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          'RFP extraction failed. Try shortening the text or retry in a moment.'
      );
      setExtraction(null);
    } finally {
      setExtracting(false);
    }
  };

  const apply = () => {
    if (!extraction || !onApply) return;
    onApply(extraction);
    onClose();
    reset();
  };

  function Field({
    label,
    value
  }: {
    label: string;
    value: string | number | undefined;
  }) {
    return (
      <Box sx={{ alignItems: 'baseline', display: 'flex', gap: 1, py: 0.5 }}>
        <Typography
          color="secondary.400"
          sx={{ minWidth: 180 }}
          variant="body_100"
        >
          {label}
        </Typography>
        <Typography variant="body_200">
          {value == null || value === '' ? '—' : String(value)}
        </Typography>
      </Box>
    );
  }

  return (
    <Drawer
      PaperProps={{ sx: { width: { md: 640, xs: '100%' } } }}
      anchor="right"
      onClose={() => {
        onClose();
        // Keep the extraction visible if drawer re-opens; reset on explicit button.
      }}
      open={open}
    >
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          p: 2
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          <Typography fontWeight="bold" variant="h6">
            Parse RFP
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        <Typography color="secondary.400" sx={{ mb: 2 }} variant="body_200">
          Paste the client&apos;s scope-of-work / RFP below. Claude will
          extract the bidding inputs so you can pre-fill Workloading.
          {clientNameHint ? ` Client: ${clientNameHint}.` : ''}
        </Typography>

        <TextField
          fullWidth
          multiline
          disabled={extracting}
          maxRows={18}
          minRows={8}
          onChange={(e) => setRfpText(e.target.value)}
          placeholder="Paste the RFP text here..."
          value={rfpText}
        />

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            mt: 2
          }}
        >
          <Typography color="secondary.400" variant="body_100">
            {rfpText.trim().length} characters · Runs ~3–8 seconds
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              disabled={extracting}
              onClick={reset}
              size="small"
              variant="text"
            >
              Clear
            </Button>
            <Button
              color="primary"
              disabled={extracting || rfpText.trim().length < 20}
              onClick={run}
              size="small"
              startIcon={extracting ? undefined : <AutoAwesomeIcon fontSize="small" />}
              variant="contained"
            >
              {extracting ? (
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  <CircularProgress color="inherit" size={14} />
                  <span>Extracting…</span>
                </Box>
              ) : (
                'Extract with AI'
              )}
            </Button>
          </Box>
        </Box>

        {error && (
          <Typography color="error.main" sx={{ mt: 2 }} variant="body_100">
            {error}
          </Typography>
        )}

        {extraction && (
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
                mb: 1.5
              }}
            >
              <Typography fontWeight="bold" variant="body_300">
                Extraction
              </Typography>
              <Chip
                label={`${confidenceLabel(extraction.confidence)} confidence · ${(
                  (extraction.confidence ?? 0) * 100
                ).toFixed(0)}%`}
                size="small"
                sx={{
                  bgcolor: confidenceColor(extraction.confidence),
                  color: 'white',
                  fontSize: '0.72rem',
                  fontWeight: 700
                }}
              />
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Field label="Site type" value={extraction.siteType} />
            <Field
              label="Suggested service"
              value={extraction.suggestedServiceCode}
            />
            <Field
              label="Total cleanable SF"
              value={
                extraction.totalCleanableSqft
                  ? extraction.totalCleanableSqft.toLocaleString()
                  : undefined
              }
            />
            <Field label="Frequency" value={extraction.frequency} />
            <Field
              label="Visit days"
              value={extraction.visitDays?.join(', ')}
            />
            <Field
              label="Operating weeks / yr"
              value={extraction.operatingWeeksPerYear}
            />
            <Field
              label="Hours per visit"
              value={extraction.hoursPerVisit}
            />
            <Field label="Crew size" value={extraction.crewSize} />
            <Field label="Site density" value={extraction.siteDensity} />
            <Field label="Soil level" value={extraction.soilLevel} />
            <Field
              label="Contract duration"
              value={
                extraction.contractDurationMonths
                  ? `${extraction.contractDurationMonths} mo`
                  : undefined
              }
            />
            <Field label="Start date" value={extraction.startDate} />
            <Field
              label="Client price expectation"
              value={
                extraction.clientPriceExpectation != null
                  ? money(extraction.clientPriceExpectation)
                  : undefined
              }
            />

            {extraction.scopeItems && extraction.scopeItems.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography color="secondary.400" variant="body_100">
                  Scope items (candidate checklist)
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                  {extraction.scopeItems.map((item) => (
                    <Typography
                      component="li"
                      key={`scope-${item}`}
                      variant="body_200"
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {extraction.requirements && extraction.requirements.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography color="secondary.400" variant="body_100">
                  Requirements
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                  {extraction.requirements.map((item) => (
                    <Typography
                      component="li"
                      key={`req-${item}`}
                      variant="body_200"
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {extraction.painPoints && extraction.painPoints.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography color="secondary.400" variant="body_100">
                  Client pain points
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                  {extraction.painPoints.map((item) => (
                    <Typography
                      component="li"
                      key={`pain-${item}`}
                      variant="body_200"
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {extraction.notes && (
              <Box sx={{ mt: 1.5 }}>
                <Typography color="secondary.400" variant="body_100">
                  Notes
                </Typography>
                <Typography variant="body_200">{extraction.notes}</Typography>
              </Box>
            )}

            {onApply && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  color="primary"
                  onClick={apply}
                  size="small"
                  variant="contained"
                >
                  Apply to this lead
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
ParseRfpDrawer.defaultProps = ParseRfpDrawerDefaults;

export default ParseRfpDrawer;
